import { useState, useEffect } from 'react';
import { openMail } from '../../utils/openMail';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {
  IoMenuOutline,
  IoCloseOutline,
  IoLogOutOutline,
  IoChatbubbleEllipsesOutline,
  IoSettingsOutline,
} from 'react-icons/io5';
import {
  MdDashboard,
  MdStorage,
  MdPayment,
  MdAccountBalance,
  MdReceipt,
} from 'react-icons/md';
import { useAuth } from '../../context/AuthContext';
import { PARTNER_MENU, PARTNER_SIGNUP_CATEGORIES } from '../../config/ieumConfig';
import ieumLogo from '../../assets/ieum-logo.png';

const ICONS = {
  MdDashboard,
  MdStorage,
  MdPayment,
  MdAccountBalance,
  MdReceipt,
  IoChatbubbleEllipsesOutline,
  IoSettingsOutline,
};

const PartnerLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData, signOut } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mailToast, setMailToast] = useState(false);

  const handleMailClick = async (e) => {
    e.preventDefault();
    const { copied } = await openMail('biz@ieum.in');
    if (copied) {
      setMailToast(true);
      setTimeout(() => setMailToast(false), 2500);
    }
  };

  // 라우트 변경 시 드로어 닫기
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // 드로어 열렸을 때 body scroll 잠금
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [drawerOpen]);

  const handleLogout = async () => {
    await signOut();
    navigate('/partner/login', { replace: true });
  };

  const isActive = (path) => {
    if (path === '/partner') return location.pathname === '/partner';
    return location.pathname.startsWith(path);
  };

  const email = userData?.email || '';

  // 업체가 제공하는 서비스 카테고리 → 드로어 배지 ("이사 +4" 형식)
  const categoryLabels = (userData?.service_categories || [])
    .map((id) => PARTNER_SIGNUP_CATEGORIES.find((c) => c.id === id)?.label || id);
  const categoryBadge = (() => {
    if (!categoryLabels.length) return null;
    return categoryLabels.length > 1 ? `${categoryLabels[0]} +${categoryLabels.length - 1}` : categoryLabels[0];
  })();
  const categoryFullText = categoryLabels.join(', ');

  const renderMenu = () =>
    PARTNER_MENU.map((item) => {
      const Icon = ICONS[item.icon] || MdDashboard;
      const active = isActive(item.path);
      return (
        <MenuItem
          key={item.key}
          type="button"
          $active={active}
          onClick={() => navigate(item.path)}
        >
          <Icon size={20} />
          <MenuLabel>{item.label}</MenuLabel>
        </MenuItem>
      );
    });

  return (
    <Wrapper>
      {/* ── PC 사이드바 ── */}
      <PcSidebar>
        <PcSidebarHeader onClick={() => navigate('/partner')}>
          <BrandLogo src={ieumLogo} alt="이음" />
          <PcBrandText>
            <BrandName>이음 제휴업체</BrandName>
            <BrandRole>제휴 포털</BrandRole>
          </PcBrandText>
          {categoryBadge && (
            <CategoryBadgeWrap>
              <DrawerCategoryBadge title={categoryFullText}>{categoryBadge}</DrawerCategoryBadge>
              {categoryLabels.length > 0 && (
                <CategoryTooltip>
                  <CategoryTooltipTitle>업종 전체</CategoryTooltipTitle>
                  <CategoryTooltipList>
                    {categoryLabels.map((label) => (
                      <CategoryTooltipItem key={label}>{label}</CategoryTooltipItem>
                    ))}
                  </CategoryTooltipList>
                </CategoryTooltip>
              )}
            </CategoryBadgeWrap>
          )}
        </PcSidebarHeader>

        <DrawerProfile>
          <DrawerEmail>{email}</DrawerEmail>
          <DrawerRoleBadge>제휴업체</DrawerRoleBadge>
        </DrawerProfile>

        <LogoutBtn type="button" onClick={handleLogout}>
          <IoLogOutOutline size={18} />
          로그아웃
        </LogoutBtn>

        <MenuList>{renderMenu()}</MenuList>

        <DrawerFooter>
          <FooterLine>문의처</FooterLine>
          <FooterLine>e-mail : <FooterEmail href="mailto:biz@ieum.in" onClick={handleMailClick}>biz@ieum.in</FooterEmail></FooterLine>
        </DrawerFooter>
      </PcSidebar>

      <Main>
      {/* ── 헤더 (모바일) ── */}
      <Header>
        <HeaderInner>
          <MenuBtn type="button" onClick={() => setDrawerOpen(true)} aria-label="메뉴">
            <IoMenuOutline size={26} />
          </MenuBtn>
          <Brand onClick={() => navigate('/partner')}>
            <BrandLogo src={ieumLogo} alt="이음" />
            <BrandText>
              <BrandName>이음 제휴업체</BrandName>
              <BrandRole>제휴 포털</BrandRole>
            </BrandText>
          </Brand>
          {categoryBadge && (
            <HeaderCategoryBadge title={categoryFullText}>{categoryBadge}</HeaderCategoryBadge>
          )}
        </HeaderInner>
      </Header>

      {/* ── 콘텐츠 ── */}
      <Content>
        <Outlet />
      </Content>

      {/* ── PC 하단 풋터 (구분선만) ── */}
      <PcFooter />
      </Main>

      {/* ── 드로어 (모바일) ── */}
      {drawerOpen && (
        <DrawerOverlay onClick={() => setDrawerOpen(false)}>
          <Drawer onClick={(e) => e.stopPropagation()}>
            <DrawerHeader>
              <DrawerLogoRow>
                <BrandLogo src={ieumLogo} alt="이음" />
                {categoryBadge && (
            <CategoryBadgeWrap>
              <DrawerCategoryBadge title={categoryFullText}>{categoryBadge}</DrawerCategoryBadge>
              {categoryLabels.length > 0 && (
                <CategoryTooltip>
                  <CategoryTooltipTitle>업종 전체</CategoryTooltipTitle>
                  <CategoryTooltipList>
                    {categoryLabels.map((label) => (
                      <CategoryTooltipItem key={label}>{label}</CategoryTooltipItem>
                    ))}
                  </CategoryTooltipList>
                </CategoryTooltip>
              )}
            </CategoryBadgeWrap>
          )}
              </DrawerLogoRow>
              <DrawerCloseBtn type="button" onClick={() => setDrawerOpen(false)}>
                <IoCloseOutline size={24} />
              </DrawerCloseBtn>
            </DrawerHeader>

            <DrawerProfile>
              <DrawerEmail>{email}</DrawerEmail>
              <DrawerRoleBadge>제휴업체</DrawerRoleBadge>
            </DrawerProfile>

            <LogoutBtn type="button" onClick={handleLogout}>
              <IoLogOutOutline size={18} />
              로그아웃
            </LogoutBtn>

            <MenuList>
              {PARTNER_MENU.map((item) => {
                const Icon = ICONS[item.icon] || MdDashboard;
                const active = isActive(item.path);
                return (
                  <MenuItem
                    key={item.key}
                    type="button"
                    $active={active}
                    onClick={() => navigate(item.path)}
                  >
                    <Icon size={20} />
                    <MenuLabel>{item.label}</MenuLabel>
                  </MenuItem>
                );
              })}
            </MenuList>

            <DrawerFooter>
              <FooterLine>문의처</FooterLine>
              <FooterLine>e-mail : <FooterEmail href="mailto:biz@ieum.in" onClick={handleMailClick}>biz@ieum.in</FooterEmail></FooterLine>
            </DrawerFooter>
          </Drawer>
        </DrawerOverlay>
      )}

      {mailToast && (
        <MailToast>biz@ieum.in 주소가 복사됐어요</MailToast>
      )}
    </Wrapper>
  );
};

export default PartnerLayout;

// ───── 스타일 ─────
const Wrapper = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.borderLight};
  display: flex;
  flex-direction: column;

  @media (min-width: 1024px) {
    flex-direction: row;
    align-items: stretch;
  }
`;

const PcSidebar = styled.aside`
  display: none;

  @media (min-width: 1024px) {
    display: flex;
    flex-direction: column;
    width: 260px;
    flex-shrink: 0;
    background: #fff;
    border-right: 1px solid #e2e6ec;
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.04);
    position: sticky;
    top: 0;
    height: 100vh;
    overflow: visible; /* CategoryTooltip이 우측으로 튀어나오도록 clip 해제 */
  }
`;

const PcSidebarHeader = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: 20px ${({ theme }) => theme.spacing.lg};
  background: none;
  border: 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  cursor: pointer;
  text-align: left;
`;

const PcBrandText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  line-height: 1.3;
`;

const Main = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: #fff;
`;

const PcFooter = styled.footer`
  display: none;

  @media (min-width: 1024px) {
    display: block;
    height: 100px;
    background: #1F2937;
    border-top: 1px solid #111827;
    margin-top: auto;
  }
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 50;
  background: ${({ theme }) => theme.colors.bgWhite};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};

  @media (min-width: 1024px) {
    display: none;
  }
`;

const HeaderInner = styled.div`
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${({ theme }) => theme.spacing.md};
`;

const MenuBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: none;
  border: 0;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  padding: 0;
`;

const Brand = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  background: none;
  border: 0;
  cursor: pointer;
  padding: 0;
`;

const BrandLogo = styled.img`
  width: 28px;
  height: 28px;
  object-fit: contain;
`;

const BrandText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.2;
`;

const BrandName = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
`;

const BrandRole = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.textLight};
`;

const Content = styled.main`
  flex: 1;
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.lg};
  padding-bottom: 60px;

  @media (min-width: 1024px) {
    max-width: 1180px;
    padding: 32px 40px 60px;
  }
`;

const DrawerOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 100;
  display: flex;
  justify-content: flex-start;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const Drawer = styled.aside`
  width: 280px;
  max-width: 85vw;
  height: 100vh;
  background: #fff;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  animation: slideIn 0.25s ease;

  @keyframes slideIn {
    from { transform: translateX(-100%); }
    to { transform: translateX(0); }
  }
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.base} ${({ theme }) => theme.spacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const DrawerLogoRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const DrawerCategoryBadge = styled.span`
  font-size: 11px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primary}15;
  padding: 3px 8px;
  border-radius: ${({ theme }) => theme.radius.full};
  cursor: help;
`;

const CategoryBadgeWrap = styled.span`
  position: relative;
  display: inline-flex;

  &:hover > span + span { opacity: 1; visibility: visible; transform: translateY(0); }
`;

const CategoryTooltip = styled.span`
  position: absolute;
  left: 0;
  top: calc(100% + 8px);
  transform: translateY(-4px);
  min-width: 160px;
  background: #ffffff;
  border: 1px solid #E5E7EB;
  box-shadow: 0 8px 24px rgba(17, 24, 39, 0.12);
  color: ${({ theme }) => theme.colors.text};
  padding: 12px 14px;
  border-radius: 10px;
  font-size: 12px;
  opacity: 0;
  visibility: hidden;
  transition: all 0.15s;
  pointer-events: none;
  z-index: 9999;
  text-align: left;
`;

const CategoryTooltipTitle = styled.span`
  display: block;
  font-size: 11px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 6px;
`;

const CategoryTooltipList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CategoryTooltipItem = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  white-space: nowrap;

  &::before {
    content: '';
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #9CA3AF;
    flex-shrink: 0;
  }
`;

const HeaderCategoryBadge = styled.span`
  font-size: 10px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primary}15;
  padding: 2px 7px;
  border-radius: ${({ theme }) => theme.radius.full};
  margin-left: 6px;
  cursor: help;
  white-space: nowrap;
`;

const DrawerCloseBtn = styled.button`
  display: flex;
  background: none;
  border: 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  padding: 4px;
`;

const DrawerProfile = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.base} ${({ theme }) => theme.spacing.lg};
`;

const DrawerEmail = styled.div`
  flex: 1;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DrawerRoleBadge = styled.span`
  font-size: 11px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primary}15;
  padding: 3px 8px;
  border-radius: ${({ theme }) => theme.radius.full};
  flex-shrink: 0;
`;

const LogoutBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin: 0 ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.base};
  padding: 12px;
  background: ${({ theme }) => theme.colors.borderLight};
  color: ${({ theme }) => theme.colors.text};
  border: 0;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;

  &:hover { background: ${({ theme }) => theme.colors.border}; }
`;

const MenuList = styled.nav`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: ${({ theme }) => theme.spacing.sm} 0;
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: 14px ${({ theme }) => theme.spacing.lg};
  background: ${({ $active, theme }) => ($active ? `${theme.colors.primary}10` : 'transparent')};
  border: 0;
  border-left: 3px solid ${({ $active, theme }) => ($active ? theme.colors.primary : 'transparent')};
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.text)};
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ $active, theme }) =>
    $active ? theme.fontWeight.semibold : theme.fontWeight.medium};
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.borderLight};
  }
`;

const MenuLabel = styled.span`
  flex: 1;
`;

const DrawerFooter = styled.div`
  padding: ${({ theme }) => theme.spacing.base} ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const FooterLine = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.6;
`;

const FooterEmail = styled.a`
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: underline;
  cursor: pointer;
`;

const MailToast = styled.div`
  position: fixed;
  bottom: 28px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(17, 24, 39, 0.92);
  color: #fff;
  padding: 12px 18px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  z-index: 9999;
  box-shadow: 0 4px 16px rgba(0,0,0,0.18);
`;
